// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Path safety for incoming files. A file frame carries a folder-relative path
 * chosen by the *remote* peer, and we are about to create directories and write
 * bytes under our own chosen folder handle. Nothing here may be allowed to
 * escape that folder, so this is a hard security boundary and is unit-tested
 * accordingly. When in doubt, reject.
 */

/** Longest total path we will accept, and longest single segment. */
const MAX_PATH = 1024;
const MAX_SEGMENT = 255;

// Reserved device names on Windows-family filesystems (case-insensitive).
const WINDOWS_RESERVED = new Set([
  'con', 'prn', 'aux', 'nul',
  'com1', 'com2', 'com3', 'com4', 'com5', 'com6', 'com7', 'com8', 'com9',
  'lpt1', 'lpt2', 'lpt3', 'lpt4', 'lpt5', 'lpt6', 'lpt7', 'lpt8', 'lpt9',
]);

/** True if the string contains any C0 control character or DEL (0x7f). */
function hasControlChar(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c <= 0x1f || c === 0x7f) return true;
  }
  return false;
}

/**
 * Normalise a remote-supplied path to a safe list of directory/file segments,
 * or return `null` if it must be rejected. Rejects absolute paths, drive
 * letters, `.`/`..` traversal, empty or whitespace-only segments, control
 * characters, over-long components, and Windows reserved names.
 */
export function safeSegments(path: string): string[] | null {
  if (typeof path !== 'string') return null;
  if (path.length === 0 || path.length > MAX_PATH) return null;
  if (hasControlChar(path)) return null;
  // Normalise Windows separators to POSIX, then split.
  const unified = path.replace(/\\/g, '/');
  // A leading slash is absolute; a drive letter (C:) is absolute too.
  if (unified.startsWith('/')) return null;
  if (/^[a-zA-Z]:/.test(unified)) return null;

  const raw = unified.split('/');
  const segs: string[] = [];
  for (const s of raw) {
    if (s === '') return null; // empty segment: leading/trailing/double slash
    if (s === '.' || s === '..') return null; // traversal
    if (s.length > MAX_SEGMENT) return null;
    if (s.trim().length === 0) return null; // whitespace-only
    // Trailing dot/space is stripped by Windows and can mask a reserved name.
    if (/[. ]$/.test(s)) return null;
    const base = s.split('.')[0].toLowerCase();
    if (WINDOWS_RESERVED.has(base)) return null;
    segs.push(s);
  }
  if (segs.length === 0) return null;
  return segs;
}

/** True if a remote path is safe to write under our folder handle. */
export function isSafePath(path: string): boolean {
  return safeSegments(path) !== null;
}
